import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonPage, IonSpinner, IonTitle, IonToolbar } from '@ionic/react';
import { useParams } from 'react-router';
import { collettivaDietLabel } from '@trackeat/shared';
import { BedCard, DietPickerModal, BEDS_CSS, type BedRowUI } from './bedForm.js';

// T-438/T-441/T-443 — PAGINA PUBBLICA dell'ordine per LETTO del reparto (dal QR,
// senza login). Stessa UI dell'app (BedCard + DietPickerModal condivisi).
// - Lo "Sblocca" valida il PIN LATO SERVER e carica l'ordine del giorno (memoria):
//   PIN errato → bloccato subito, niente form compilato a vuoto.
// - Riaprendo il QR il reparto ritrova ciò che ha inviato (consultazione + storico).
//   Se ancora bozza (in controllo preposto) → modificabile; se il preposto l'ha
//   confermato (submitted+) → sola lettura.
// - Export PDF dell'ordine (stampa in-app, niente browser che si incastra).

const API_BASE = 'https://trackeat.eu';

type Meal = 'colazione' | 'pranzo' | 'cena';
const MEALS: Array<[Meal, string]> = [['pranzo', 'Pranzo'], ['cena', 'Cena'], ['colazione', 'Colazione']];
const MEAL_LABEL: Record<Meal, string> = { colazione: 'Colazione', pranzo: 'Pranzo', cena: 'Cena' };
const STATUS_LABEL: Record<string, string> = { draft: 'In controllo dal preposto', submitted: 'Inviato / confermato', accepted: 'Accettato', in_production: 'In produzione', fulfilled: 'Evaso', rejected: 'Respinto' };
const todayIso = (): string => new Date().toISOString().slice(0, 10);
const emptyOrder = (): Record<Meal, BedRowUI[]> => ({ colazione: [], pranzo: [], cena: [] });
const allEditable = (): Record<Meal, boolean> => ({ colazione: true, pranzo: true, cena: true });
const noStatus = (): Record<Meal, string | null> => ({ colazione: null, pranzo: null, cena: null });

interface WardInfo { dest_name: string; ward_name: string }
// pranzo/cena sono ordini SEPARATI → editabilità e stato sono PER SERVIZIO.
interface LoadResult { editableByService: Record<Meal, boolean>; statusByService: Record<Meal, string | null>; byService: Record<Meal, Array<{ letto: string; diet_code: string; note: string }>> }

let seq = 0;
const newKey = (): string => `b${++seq}`;
const escHtml = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

export default function RepartoPublicPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [ward, setWard] = useState<WardInfo | null>(null);
  const [pin, setPin] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [busy, setBusy] = useState(false); // unlock/reload in corso
  const [date, setDate] = useState(todayIso());
  const [meal, setMeal] = useState<Meal>('pranzo');
  const [order, setOrder] = useState<Record<Meal, BedRowUI[]>>(emptyOrder());
  const [editableByService, setEditableByService] = useState<Record<Meal, boolean>>(allEditable());
  const [statusByService, setStatusByService] = useState<Record<Meal, string | null>>(noStatus());
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'err' | 'ok'; text: string } | null>(null);
  const [printHtml, setPrintHtml] = useState<string | null>(null);
  const printRef = useRef<HTMLIFrameElement>(null);

  const validToken = /^[a-z0-9]{20,64}$/.test(token);
  const beds = order[meal];
  // editabilità/stato del servizio ATTUALE (derivati dalle mappe per-servizio).
  const editable = editableByService[meal];
  const orderStatus = statusByService[meal];

  useEffect(() => {
    if (!validToken) { setLoading(false); return; }
    let mounted = true;
    void (async () => {
      try {
        const r = await fetch(`${API_BASE}/reparto/${token}?info=1`, { headers: { accept: 'application/json' } });
        if (!mounted) return;
        if (r.ok) setWard((await r.json()) as WardInfo);
      } catch { /* rete → resta null (mostra "non valido") */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [token, validToken]);

  // Carica (o ricarica) l'ordine del giorno dal server, validando il PIN.
  // Ritorna 'wrong_pin' | 'err' | 'ok'. Popola `order`/`status`/`editable`.
  // `openFirstIfEmpty`: se l'ordine è vuoto (nuovo), apre subito il primo letto + il
  // picker dieta (così l'inserimento parte immediato, poi avanza da solo letto per letto).
  const loadOrder = useCallback(async (pinVal: string, dateVal: string, openFirstIfEmpty = false): Promise<'wrong_pin' | 'err' | 'ok'> => {
    try {
      const r = await fetch(`${API_BASE}/reparto/${token}`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'load', pin: pinVal, date: dateVal }),
      });
      if (r.status === 403) return 'wrong_pin';
      if (!r.ok) return 'err';
      const j = (await r.json()) as LoadResult;
      const next = emptyOrder();
      let total = 0;
      (['colazione', 'pranzo', 'cena'] as Meal[]).forEach((svc) => {
        next[svc] = (j.byService?.[svc] ?? []).map((b) => ({ key: newKey(), letto: b.letto, dietCode: b.diet_code, note: b.note }));
        total += next[svc].length;
      });
      const editableNext = { ...allEditable(), ...(j.editableByService ?? {}) };
      if (openFirstIfEmpty && total === 0 && editableNext.pranzo !== false) {
        const key = newKey();
        next.pranzo = [{ key, letto: '1', dietCode: '', note: '' }];
        setMeal('pranzo');
        setPickerFor(key);
      }
      setOrder(next);
      setEditableByService(editableNext);
      setStatusByService({ ...noStatus(), ...(j.statusByService ?? {}) });
      return 'ok';
    } catch { return 'err'; }
  }, [token]);

  const nextLetto = useCallback((svc: Meal): string => {
    let mx = 0;
    for (const b of order[svc]) { const n = parseInt(b.letto, 10); if (Number.isFinite(n) && n > mx) mx = n; }
    return String(mx + 1);
  }, [order]);

  const addBed = useCallback((autoPick = true) => {
    const key = newKey();
    setOrder((p) => ({ ...p, [meal]: [...p[meal], { key, letto: nextLetto(meal), dietCode: '', note: '' }] }));
    if (autoPick) setPickerFor(key);
  }, [meal, nextLetto]);
  const patchBed = useCallback((key: string, patch: Partial<BedRowUI>) => {
    setOrder((p) => ({ ...p, [meal]: p[meal].map((b) => (b.key === key ? { ...b, ...patch } : b)) }));
  }, [meal]);
  const removeBed = useCallback((key: string) => {
    setOrder((p) => ({ ...p, [meal]: p[meal].filter((b) => b.key !== key) }));
  }, [meal]);

  const unlock = useCallback(async () => {
    if (!pin.trim() || busy) return;
    setBusy(true); setMsg(null);
    const res = await loadOrder(pin.trim(), date, true); // ordine nuovo → apre subito 1° letto + picker
    setBusy(false);
    if (res === 'wrong_pin') { setMsg({ kind: 'err', text: 'PIN errato. Ricontrolla il PIN del reparto.' }); return; }
    if (res === 'err') { setMsg({ kind: 'err', text: 'Errore di rete. Riprova.' }); return; }
    setUnlocked(true);
  }, [pin, busy, date, loadOrder]);

  // Cambio data → ricarica l'ordine di quel giorno (PIN già validato).
  const changeDate = useCallback(async (d: string) => {
    const nd = d || todayIso();
    setDate(nd);
    if (!unlocked) return;
    setBusy(true); setMsg(null);
    const res = await loadOrder(pin.trim(), nd);
    setBusy(false);
    if (res !== 'ok') setMsg({ kind: 'err', text: 'Non riesco a caricare quel giorno. Riprova.' });
  }, [unlocked, pin, loadOrder]);

  const readyBeds = beds.filter((b) => b.letto.trim() && b.dietCode);
  const totalBeds = useMemo(() => (['colazione', 'pranzo', 'cena'] as Meal[]).reduce((n, s) => n + order[s].filter((b) => b.letto.trim() && b.dietCode).length, 0), [order]);

  const send = useCallback(async () => {
    setMsg(null);
    if (readyBeds.length === 0) { setMsg({ kind: 'err', text: 'Aggiungi almeno un letto con la dieta.' }); return; }
    setSending(true);
    try {
      const r = await fetch(`${API_BASE}/reparto/${token}`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ pin: pin.trim(), date, meal_service: meal, beds: readyBeds.map((b) => ({ letto: b.letto.trim(), diet_code: b.dietCode, note: b.note.trim() })) }),
      });
      const j = (await r.json().catch(() => ({}))) as { ok?: boolean; count?: number; error?: string };
      if (r.status === 200 && j.ok) {
        setMsg({ kind: 'ok', text: `✓ Inviato al preposto (${j.count} letti per ${MEAL_LABEL[meal].toLowerCase()}).` });
        // QUESTO servizio è ora una bozza in controllo dal preposto. NON ricarico dal
        // server: sovrascriverei i letti digitati (magari di altri servizi) non inviati.
        setStatusByService((s) => ({ ...s, [meal]: 'draft' }));
        setEditableByService((e) => ({ ...e, [meal]: true }));
      } else if (r.status === 409) {
        // Il preposto ha già confermato QUESTO servizio → ricarico in sola lettura.
        setMsg({ kind: 'err', text: `Il preposto ha già confermato ${MEAL_LABEL[meal].toLowerCase()}: non puoi più modificarlo da qui.` });
        await loadOrder(pin.trim(), date);
      } else if (r.status === 403) {
        setUnlocked(false); setMsg({ kind: 'err', text: 'PIN errato. Ricontrolla il PIN del reparto.' });
      } else {
        setMsg({ kind: 'err', text: 'Invio non riuscito. Riprova.' });
      }
    } catch {
      setMsg({ kind: 'err', text: 'Errore di rete. Riprova.' });
    } finally { setSending(false); }
  }, [readyBeds, token, pin, date, meal, loadOrder]);

  // ── Export PDF: foglio A4 dell'ordine del reparto (tutti i servizi del giorno). ──
  const buildPrintHtml = useCallback((): string => {
    if (!ward) return '';
    const dateTxt = (() => { const [y, m, d] = date.split('-'); return `${d}/${m}/${y}`; })();
    const sections = (['colazione', 'pranzo', 'cena'] as Meal[]).map((svc) => {
      const rows = order[svc].filter((b) => b.letto.trim() && b.dietCode);
      if (rows.length === 0) return '';
      const st = statusByService[svc];
      const stTxt = st ? (STATUS_LABEL[st] ?? st) : 'Bozza';
      const trs = rows
        .slice()
        .sort((a, b) => (parseInt(a.letto, 10) || 0) - (parseInt(b.letto, 10) || 0))
        .map((b) => `<tr><td class="lt">${escHtml(b.letto)}</td><td>${escHtml(collettivaDietLabel(b.dietCode))}</td><td class="nt">${escHtml(b.note || '—')}</td></tr>`)
        .join('');
      // Stato PER SERVIZIO (pranzo/cena = ordini separati, possono avere stati diversi).
      return `<h2>${MEAL_LABEL[svc]} · ${rows.length} letti <span class="st">${escHtml(stTxt)}</span></h2>
        <table><thead><tr><th class="lt">Letto</th><th>Dieta</th><th class="nt">Note</th></tr></thead><tbody>${trs}</tbody></table>`;
    }).join('');
    const body = sections || '<p class="empty">Nessun letto nell\'ordine.</p>';
    return `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Ordine ${escHtml(ward.ward_name)} ${dateTxt}</title>
<style>
  @page{size:A4;margin:14mm}
  *{box-sizing:border-box}body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#12333a;margin:0}
  .hd{border-bottom:3px solid #0b6b63;padding-bottom:10px;margin-bottom:14px}
  .hd .k{font-size:12px;letter-spacing:.06em;text-transform:uppercase;color:#0b6b63;font-weight:700}
  .hd h1{margin:2px 0 6px;font-size:24px}
  .meta{display:flex;gap:18px;flex-wrap:wrap;font-size:13px;color:#334}
  .meta b{color:#0b6b63}
  h2{font-size:15px;margin:16px 0 6px;color:#0b6b63}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th,td{border:1px solid #cfe0dd;padding:7px 9px;text-align:left;vertical-align:top}
  th{background:#e8f4f2;font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:#0b6b63}
  td.lt,th.lt{width:64px;text-align:center;font-weight:700}
  td.nt,th.nt{width:38%;color:#475}
  tbody tr:nth-child(even){background:#f6fbfa}
  .empty{color:#889;font-style:italic}
  h2 .st{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.03em;color:#fff;background:#12a594;border-radius:6px;padding:2px 8px;margin-left:8px;vertical-align:middle}
  .foot{margin-top:22px;padding-top:8px;border-top:1px solid #dbe7e5;font-size:11px;color:#8aa}
</style></head><body>
  <div class="hd"><div class="k">${escHtml(ward.dest_name)}</div><h1>Ordine pasti — Reparto ${escHtml(ward.ward_name)}</h1>
    <div class="meta"><span>Data: <b>${dateTxt}</b></span><span>Totale letti: <b>${totalBeds}</b></span></div></div>
  ${body}
  <div class="foot">Documento generato da TrackEAT · reparto ${escHtml(ward.ward_name)} · ${dateTxt}. Copia per il reparto.</div>
</body></html>`;
  }, [ward, order, statusByService, date, totalBeds]);

  const doPrint = useCallback(() => {
    const w = printRef.current?.contentWindow;
    if (w) { w.focus(); w.print(); }
  }, []);

  const curBedForPicker = beds.find((b) => b.key === pickerFor);
  const curCode = curBedForPicker?.dietCode ?? '';
  const hasOrder = totalBeds > 0;

  return (
    <IonPage>
      <style>{`
        ${BEDS_CSS}
        .rp{max-width:640px;margin:0 auto;padding:16px}
        .rp-hero{background:linear-gradient(135deg,#0b6b63,#12a594);color:#fff;border-radius:16px;padding:16px 18px;margin-bottom:14px}
        .rp-hero .k{opacity:.85;font-size:12px;letter-spacing:.06em;text-transform:uppercase}
        .rp-hero h1{margin:2px 0 0;font-size:22px}
        .rp-card{background:#fff;border:1px solid #e5edef;border-radius:14px;padding:14px;margin-bottom:12px}
        .rp-lbl{display:block;font-size:12px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.03em;margin:0 0 4px}
        .rp-in{width:100%;box-sizing:border-box;font-size:16px;padding:11px 10px;border:1px solid #cfd8dc;border-radius:10px;background:#fff;color:#12333a}
        .rp-row{display:flex;gap:8px}
        .rp-pin{max-width:220px;margin:8px auto 0}.rp-pin .rp-in{text-align:center;letter-spacing:.3em;font-size:22px;font-weight:800}
        .rp-msg{border-radius:10px;padding:10px 12px;font-size:14px;margin-top:10px}.rp-msg.err{background:#fee2e2;color:#b91c1c}.rp-msg.ok{background:#dcfce7;color:#166534}
        .rp-banner{border-radius:10px;padding:10px 12px;font-size:13px;line-height:1.4;margin-bottom:12px}
        .rp-banner.edit{background:#eef7f5;color:#0b6b63;border:1px solid #cbe7e1}
        .rp-banner.lock{background:#fff7ed;color:#9a5b12;border:1px solid #f0d8b3}
        .rp-muted{font-size:13px;color:#64748b;line-height:1.4}
        .rp-foot{text-align:center;font-size:12px;color:#94a3b8;padding:10px 0 24px}
        .rp-print-frame{width:100%;height:60vh;border:1px solid #e5edef;border-radius:10px;background:#fff}
      `}</style>
      <IonContent>
        <div className="rp">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><IonSpinner /></div>
          ) : !ward ? (
            <div className="rp-card" style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>Link non valido o scaduto.</div>
          ) : (
            <>
              <div className="rp-hero"><div className="k">{ward.dest_name}</div><h1>Reparto {ward.ward_name}</h1></div>

              {!unlocked ? (
                <div className="rp-card">
                  <span className="rp-lbl">PIN reparto</span>
                  <div className="rp-pin"><input className="rp-in" inputMode="numeric" autoComplete="off" placeholder="••••" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void unlock(); }} /></div>
                  <div style={{ height: 10 }} />
                  <IonButton expand="block" disabled={busy} onClick={() => void unlock()}>{busy ? 'Verifica…' : 'Sblocca'}</IonButton>
                  {msg && <div className={`rp-msg ${msg.kind}`}>{msg.text}</div>}
                  <p className="rp-muted" style={{ marginTop: 12 }}>Inserisci il PIN che trovi sul foglio del reparto. Nessun login richiesto.</p>
                </div>
              ) : (
                <>
                  {/* Banner del servizio ATTUALE (pranzo/cena sono ordini separati). */}
                  {!editable
                    ? <div className="rp-banner lock">🔒 <b>{MEAL_LABEL[meal]}</b> {orderStatus ? (STATUS_LABEL[orderStatus] ?? orderStatus).toLowerCase() : 'confermato'} dal preposto: sola lettura. Puoi comunque esportarlo in PDF qui sotto.</div>
                    : orderStatus === 'draft'
                      ? <div className="rp-banner edit">✏️ Hai già inviato <b>{MEAL_LABEL[meal].toLowerCase()}</b> ({readyBeds.length} letti). Puoi modificarlo e reinviarlo finché il preposto non lo conferma.</div>
                      : null}

                  <div className="rp-card">
                    <div className="rp-row">
                      <div style={{ flex: 1 }}><span className="rp-lbl">Data</span><input type="date" className="rp-in" value={date} disabled={busy} onChange={(e) => void changeDate(e.target.value)} /></div>
                      <div style={{ flex: 1 }}>
                        <span className="rp-lbl">Servizio</span>
                        <select className="rp-in" value={meal} onChange={(e) => setMeal(e.target.value as Meal)}>
                          {MEALS.map(([v, l]) => <option key={v} value={v}>{l}{order[v].filter((b) => b.letto.trim() && b.dietCode).length > 0 ? ` (${order[v].filter((b) => b.letto.trim() && b.dietCode).length})` : ''}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {busy && <div className="rp-card" style={{ textAlign: 'center' }}><IonSpinner name="dots" /></div>}

                  {beds.length === 0 && !busy && (
                    <div className="rp-card rp-muted">{editable ? `Nessun letto per ${MEAL_LABEL[meal].toLowerCase()}. Aggiungine uno qui sotto.` : `Nessun letto per ${MEAL_LABEL[meal].toLowerCase()}.`}</div>
                  )}
                  {beds.map((b) => (
                    <BedCard key={b.key} bed={b} readOnly={!editable} active={pickerFor === b.key} onPatch={(patch) => patchBed(b.key, patch)} onRemove={() => removeBed(b.key)} onOpenPicker={() => setPickerFor(b.key)} />
                  ))}

                  {editable && <IonButton expand="block" fill="outline" onClick={() => addBed(true)}>+ Aggiungi letto</IonButton>}
                  <div style={{ height: 12 }} />
                  {editable && (
                    <>
                      <div className="rp-card"><div className="rp-muted">{readyBeds.length} letti pronti per {MEAL_LABEL[meal].toLowerCase()} su {beds.length} inseriti.</div></div>
                      <IonButton expand="block" disabled={sending} onClick={() => void send()}>{sending ? 'Invio…' : (orderStatus === 'draft' ? `Aggiorna e reinvia ${MEAL_LABEL[meal].toLowerCase()}` : `Invia ${MEAL_LABEL[meal].toLowerCase()} al preposto`)}</IonButton>
                    </>
                  )}
                  {hasOrder && <IonButton expand="block" fill="clear" onClick={() => setPrintHtml(buildPrintHtml())}>📄 Esporta / Stampa PDF dell'ordine</IonButton>}
                  {msg && <div className={`rp-msg ${msg.kind}`}>{msg.text}</div>}
                </>
              )}
              <div className="rp-foot">TrackEAT · l'ordine arriva al preposto per il controllo</div>
            </>
          )}
        </div>
      </IonContent>

      <DietPickerModal
        open={pickerFor !== null}
        currentCode={curCode}
        letto={curBedForPicker?.letto}
        onSelect={(code) => {
          // Immessa la dieta: procedi automaticamente al letto SUCCESSIVO (senza
          // ri-cliccare "Aggiungi letto"), così l'inserimento è rapido e continuo.
          if (pickerFor) { patchBed(pickerFor, { dietCode: code }); addBed(true); }
        }}
        onClose={() => {
          // Chiuso senza scegliere: il letto in eccesso (vuoto) viene rimosso.
          const curBed = order[meal].find((x) => x.key === pickerFor);
          if (curBed && !curBed.dietCode) removeBed(curBed.key);
          setPickerFor(null);
        }}
      />

      {/* Anteprima/stampa PDF IN-APP (iframe), non window.open → niente browser che si incastra. */}
      <IonModal isOpen={printHtml !== null} onDidDismiss={() => setPrintHtml(null)}>
        <IonHeader><IonToolbar><IonTitle>Ordine — PDF</IonTitle><IonButtons slot="end"><IonButton onClick={() => setPrintHtml(null)}>Chiudi</IonButton></IonButtons></IonToolbar></IonHeader>
        <IonContent className="ion-padding">
          <iframe ref={printRef} className="rp-print-frame" srcDoc={printHtml ?? ''} title="Anteprima ordine" />
          <div style={{ height: 12 }} />
          <IonButton expand="block" onClick={doPrint}>Stampa / Salva come PDF</IonButton>
          <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center' }}>Nella finestra di stampa scegli “Salva come PDF” per conservarlo.</p>
        </IonContent>
      </IonModal>
    </IonPage>
  );
}
